import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  Popover
} from '@mui/material';
import { Add, InfoOutlined, Calculate } from '@mui/icons-material';
import TimeSinceRecordRow from '../TimeSinceRecordRow';
import HoursConverterCalculatorDialog from '../../AircraftLifecycleComponents/HoursConverterCalculatorDialog';
import {
  computeAssemblyComparison,
  computeComponentSelfComparison,
  findAirframeAssembly,
  parseValueByUnit,
  formatValueByUnit
} from '../tsrComparisonUtils';

const TimeSinceRecordsSection = ({
  timeSinceRecords,
  handleAddTimeSinceRecord,
  handleUpdateTimeSinceRecord,
  handleRemoveTimeSinceRecord,
  validateYearsInput,
  validateCyclesInput,
  validateCalendarDaysInput,
  assemblies = [],
  parentAssembly,
  readOnly = false,
  labelOptions = [],
  tlbCycleOptions = [],
  tlbCycleOptionsLoading = false,
  onSelectTlbCycle
}) => {
  const [typeInfoAnchor, setTypeInfoAnchor] = useState(null);
  const [limitInfoAnchor, setLimitInfoAnchor] = useState(null);
  const [elapsedInfoAnchor, setElapsedInfoAnchor] = useState(null);
  const [hoursCalculatorOpen, setHoursCalculatorOpen] = useState(false);

  const hasAnyHours = timeSinceRecords.some(
    (r) =>
      r.limit?.unit === 'Hours' ||
      r.elapsedTime?.unit === 'Hours' ||
      r.remainingTime?.unit === 'Hours'
  );

  const { assembly: airframeAssembly } = findAirframeAssembly(assemblies || []);
  const parentAssemblyTimeSinceRecords = parentAssembly?.timeSinceRecords || [];
  const airframeTimeSinceRecords = airframeAssembly?.timeSinceRecords || [];

  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Time Since Records</Typography>
          {hasAnyHours && (
            <Tooltip title="Hours converter (decimal ⇄ HH:MM)">
              <IconButton
                size="small"
                color="primary"
                onClick={() => setHoursCalculatorOpen(true)}
                aria-label="Open hours converter calculator"
              >
                <Calculate fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={handleAddTimeSinceRecord}
          >
            Add Record
          </Button>
        )}
      </Box>
      <HoursConverterCalculatorDialog
        open={hoursCalculatorOpen}
        onClose={() => setHoursCalculatorOpen(false)}
      />
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  Type
                  <Tooltip title="Info">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => setTypeInfoAnchor(e.currentTarget)}
                      sx={{ p: 0.25 }}
                      aria-label="Time since record types info"
                    >
                      <InfoOutlined sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={Boolean(typeInfoAnchor)}
                    anchorEl={typeInfoAnchor}
                    onClose={() => setTypeInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Alert severity="info" sx={{ m: 1.5, maxWidth: 360 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Time Since Record Types:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                        <li><strong>TSN</strong>: Time Since New</li>
                        <li><strong>TSO</strong>: Time Since Overhaul</li>
                        <li><strong>TSI</strong>: Time Since Inspection</li>
                        <li><strong>TSP</strong>: Time Since Part</li>
                        <li><strong>TSR</strong>: Time Since Repair</li>
                        <li><strong>SLL</strong>: Service Life Limit</li>
                        <li><strong>OTL</strong>: Operational Time Limit</li>
                      </Box>
                    </Alert>
                  </Popover>
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>Label</TableCell>
              <TableCell>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  Life Limit (or) Frequency of THIS Component itself
                  <Tooltip title="Limit actions by type">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => setLimitInfoAnchor(e.currentTarget)}
                      sx={{ p: 0.25 }}
                      aria-label="Limit info"
                    >
                      <InfoOutlined sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={Boolean(limitInfoAnchor)}
                    anchorEl={limitInfoAnchor}
                    onClose={() => setLimitInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Alert severity="info" sx={{ m: 1.5, maxWidth: 380 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        When limit is reached:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                        <li><strong>TSN</strong>: Discard / Repair / Overhaul</li>
                        <li><strong>TSO</strong>: Overhaul</li>
                        <li><strong>TSI</strong>: Re-inspect</li>
                        <li><strong>TSP</strong>/<strong>TSR</strong>: As per maintenance program</li>
                      </Box>
                    </Alert>
                  </Popover>
                </Box>
              </TableCell>
              <TableCell>
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  Elapsed value of THIS Component itself at Installation
                  <Tooltip title="Snapshot of how much this COMPONENT has used at the moment of installation. Live elapsed will continue from this value.">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={(e) => setElapsedInfoAnchor(e.currentTarget)}
                      sx={{ p: 0.25 }}
                      aria-label="Elapsed at installation info"
                    >
                      <InfoOutlined sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={Boolean(elapsedInfoAnchor)}
                    anchorEl={elapsedInfoAnchor}
                    onClose={() => setElapsedInfoAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Alert severity="info" sx={{ m: 1.5, maxWidth: 380 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Elapsed value of THIS Component itself at Installation
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        Snapshot of how much this COMPONENT has used at the moment of installation.
                        Live elapsed will continue from this value.
                      </Typography>
                    </Alert>
                  </Popover>
                </Box>
              </TableCell>
              <TableCell>Airframe Value at Installation</TableCell>
              <TableCell>Parent Assembly&apos;s Value at Installation</TableCell>
              <TableCell sx={{ width: 110 }}>Remaining</TableCell>
              <TableCell sx={{ minWidth: 160 }}>
                <Tooltip title="For Years or Calendar Days: start (or as-on) through projected calendar due from As On Date + remaining. Red if due in ≤7 days or overdue; green otherwise.">
                  <span>Due (calendar)</span>
                </Tooltip>
              </TableCell>
              <TableCell>Due At Airframe Assembly</TableCell>
              <TableCell sx={{ width: 120 }}>Start Date</TableCell>
              <TableCell sx={{ width: 120 }}>As On Date</TableCell>
              <TableCell>Due At Parent Assembly</TableCell>
              <TableCell>Due At This Itself</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSinceRecords.map((record, index) => {
              const parentComparison = parentAssembly
                ? computeAssemblyComparison(record, parentAssemblyTimeSinceRecords)
                : null;
              const airframeComparison = airframeAssembly
                ? computeAssemblyComparison(record, airframeTimeSinceRecords)
                : null;
              const selfComparison = computeComponentSelfComparison(record);

              const unit =
                record.elapsedTime?.unit ||
                record.limit?.unit ||
                record.remainingTime?.unit ||
                '';

              const remainingStr = record.remainingTime?.value;

              // Inline Due At Parent Assembly: mirror "As compared to Assembly" logic
              const parentSnapshotStr = record.parentAssemblyValueAtInstallation?.value;
              let inlineDueAtParentAssembly = null;
              if (
                parentSnapshotStr != null &&
                String(parentSnapshotStr).trim() !== '' &&
                remainingStr != null &&
                String(remainingStr).trim() !== '' &&
                unit
              ) {
                try {
                  const installedNumeric = parseValueByUnit(parentSnapshotStr, unit);
                  const remainingNumeric = parseValueByUnit(remainingStr, unit);
                  const dueNumeric = installedNumeric + remainingNumeric;
                  inlineDueAtParentAssembly = formatValueByUnit(dueNumeric, unit);
                } catch (e) {
                  inlineDueAtParentAssembly = null;
                }
              }
              if (inlineDueAtParentAssembly == null && parentComparison?.dueAtAssembly) {
                inlineDueAtParentAssembly = parentComparison.dueAtAssembly;
              }

              // Inline Due At Airframe Assembly: mirror "As compared to Airframe" logic
              const airframeSnapshotStr = record.airframeValueAtInstallation?.value;
              let inlineDueAtAirframeAssembly = null;
              if (
                airframeSnapshotStr != null &&
                String(airframeSnapshotStr).trim() !== '' &&
                remainingStr != null &&
                String(remainingStr).trim() !== '' &&
                unit
              ) {
                try {
                  const installedNumeric = parseValueByUnit(airframeSnapshotStr, unit);
                  const remainingNumeric = parseValueByUnit(remainingStr, unit);
                  const dueNumeric = installedNumeric + remainingNumeric;
                  inlineDueAtAirframeAssembly = formatValueByUnit(dueNumeric, unit);
                } catch (e) {
                  inlineDueAtAirframeAssembly = null;
                }
              }
              if (inlineDueAtAirframeAssembly == null && airframeComparison?.dueAtAssembly) {
                inlineDueAtAirframeAssembly = airframeComparison.dueAtAssembly;
              }

              // Inline Due At This Itself: reuse self-comparison helper directly
              const inlineDueAtThisItself = selfComparison?.dueAtComponent || null;

              return (
                <TimeSinceRecordRow
                  key={index}
                  record={record}
                  index={index}
                  onUpdate={readOnly ? () => {} : handleUpdateTimeSinceRecord}
                  onRemove={readOnly ? () => {} : handleRemoveTimeSinceRecord}
                  validateYearsInput={validateYearsInput}
                  validateCyclesInput={validateCyclesInput}
                  validateCalendarDaysInput={validateCalendarDaysInput}
                  dueAtParentAssembly={inlineDueAtParentAssembly}
                  dueAtAirframeAssembly={inlineDueAtAirframeAssembly}
                  dueAtThisItself={inlineDueAtThisItself}
                  readOnly={readOnly}
                  labelOptions={labelOptions}
                  tlbCycleOptions={tlbCycleOptions}
                  tlbCycleOptionsLoading={tlbCycleOptionsLoading}
                  onSelectTlbCycle={onSelectTlbCycle}
                />
              );
            })}
            {timeSinceRecords.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} align="center">
                  No time since records. Click "Add Record" to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Legend for Time Since Record Types */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Time Since Record Types:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><strong>TSN</strong>: Time Since New</li>
            <li><strong>TSO</strong>: Time Since Overhaul</li>
            <li><strong>TSI</strong>: Time Since Inspection</li>
            <li><strong>TSP</strong>: Time Since Part</li>
            <li><strong>TSR</strong>: Time Since Repair</li>
            <li><strong>SLL</strong>: Service Life Limit</li>
            <li><strong>OTL</strong>: Operational Time Limit</li>
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, mt: 1.5 }}>
            Tracking Units:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <li><strong>Hours</strong>: Time-based tracking (HH:MM format). Use for hourly limits.</li>
            <li><strong>Years</strong>: Calendar-based tracking (YY.MM format, e.g., 2.10 = 2 years 10 months). Max 2 digits for years, 0-12 for months. Elapsed time auto-calculates from Start Date and As On Date. Use for yearly/calendar limits.</li>
            <li><strong>Calendar Days</strong>: Calendar-based tracking (numeric format). Use for daily calendar limits.</li>
            <li><strong>Cycles</strong>: Select a Technical Log Book cycle counter (labels from this aircraft&apos;s TLB format; live values from latest approved carried-forward totals). Numeric format, up to 2 decimal places.</li>
            <li><strong>Landings</strong>: Landing-based tracking (numeric format, integer). Use for landing-based limits.</li>
            <li><strong>Hobbs Meter</strong>: Hobbs meter reading (numeric format, up to 2 decimal places). Live comparison uses the HOBBS METER carried-forward total from the latest approved tech log unless mapped otherwise in Client Settings.</li>
            <li><strong>Hobbs Meter AC</strong>: Air-conditioning Hobbs meter reading (numeric format, up to 2 decimal places). Live comparison uses the HOBBS METER AC carried-forward total from the latest approved tech log unless mapped otherwise in Client Settings.</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            Note: For calendar-based records (Years), elapsed time is automatically calculated from the date range. Remaining time = Limit - Elapsed time.
          </Typography>
        </Alert>
      </Box>
    </Grid>
  );
};

export default TimeSinceRecordsSection;
